/**
 * 버튼 이름 다국어 지원 (btnNames)
 *
 * 사용법:
 * import { getBtnName } from '@src/shared/constants/btnNames';
 * <HintWrapper content={getBtnName('NEXT_STEP')} />
 */

import type { LocalizedMessages } from '../dto/EntityRepository';
import { useLanguageStore, Language } from '../store/languageStore';

export type BtnNameKey =
  // Common
  | 'REPLACE_IMAGE'
  | 'EMAIL_VERIFICATION'
  | 'OK'
  | 'CANCEL'
  | 'SAVE'
  | 'ACTIVATE'
  | 'DEACTIVATE'
  | 'UNLINK'
  | 'BEFORE'
  // OnBoarding
  | 'NEXT_STEP'
  | 'ADD_WORK'
  // Consent modal
  | 'AGREE'
  // Email verification modal
  | 'VERIFY'
  | 'RESEND'
  // Landing
  | 'MY_ARCHIVE'
  // Floating island (LeftTopIsland)
  | 'NOTICE'
  | 'SUPPORT'
  // Floating island (RightTopIsland)
  | 'MY_ARCHIVE_LABEL'
  | 'SEARCH'
  | 'INDEX'
  | 'EXHIBITION_LABEL'
  | 'EXHIBITION_SELECTED_VENUES'
  | 'EXHIBITION_VENUE_BACK'
  | 'SETTINGS'
  | 'GUIDE'
  | 'LOGOUT'
  // Support modal
  | 'SUBMIT'
  // Auth (Login / Register)
  | 'LOGIN'
  | 'REGISTER'
  // Modal actions
  | 'CHANGE'
  | 'IMPORT'
  | 'APPLY'
  | 'TAG_MATCH_ALL_LABEL'
  | 'TAG_MATCH_ANY_LABEL'
  | 'GENERATE'
  | 'SET'
  | 'FIND'
  | 'REQUEST_ACCESS'
  | 'REQUEST_VENUE_REGISTRATION'
  | 'MOVE_TO_SUBSCRIBE'
  | 'MANAGE'
  | 'SUBSCRIBE'
  | 'INVITE'
  | 'INVITING'
  | 'ACCEPT'
  | 'REJECT'
  | 'CANCELED'
  | 'RETRY'
  // Project detail — export / floor plan (ProjectElementList)
  | 'PROJECT_PORTFOLIO'
  | 'PROJECT_FLOOR_PLAN'
  ;

const btnNames: LocalizedMessages<BtnNameKey> = {
  REPLACE_IMAGE: {
    [Language.EN]: "Replace Image",
    [Language.KR]: "이미지 변경하기",
  },
  EMAIL_VERIFICATION: {
    [Language.EN]: "Email Verification ->",
    [Language.KR]: "이메일 인증하기 -> ",
  },
  OK: {
    [Language.EN]: "OK",
    [Language.KR]: "확인",
  },
  NEXT_STEP: {
    [Language.EN]: "Next ->",
    [Language.KR]: "다음 ->",
  },
  ADD_WORK: {
    [Language.EN]: "Add image",
    [Language.KR]: "작품 추가하기",
  },
  AGREE: {
    [Language.EN]: "Agree",
    [Language.KR]: "동의",
  },
  VERIFY: {
    [Language.EN]: "Verify",
    [Language.KR]: "인증 완료",
  },
  RESEND: {
    [Language.EN]: "Resend",
    [Language.KR]: "재전송",
  },
  MY_ARCHIVE: {
    [Language.EN]: "My Archive ->",
    [Language.KR]: "내 Archive ->",
  },
  NOTICE: {
    [Language.EN]: "Updates",
    [Language.KR]: "소식",
  },
  SUPPORT: {
    [Language.EN]: "Support",
    [Language.KR]: "문의",
  },
  MY_ARCHIVE_LABEL: {
    [Language.EN]: "My Archive ->",
    [Language.KR]: "나의 아카이브 ->",
  },
  SEARCH: {
    [Language.EN]: "Search",
    [Language.KR]: "검색",
  },
  INDEX: {
    [Language.EN]: "<- Index",
    [Language.KR]: "<- 목록",
  },
  EXHIBITION_LABEL: {
    [Language.EN]: "Exhibition",
    [Language.KR]: "전시",
  },
  EXHIBITION_SELECTED_VENUES: {
    [Language.EN]: "{count} venues ->",
    [Language.KR]: "{count}개 장소 ->",
  },
  EXHIBITION_VENUE_BACK: {
    [Language.EN]: "<- Venues",
    [Language.KR]: "<- 장소",
  },
  SETTINGS: {
    [Language.EN]: "Settings",
    [Language.KR]: "설정",
  },
  GUIDE: {
    [Language.EN]: "Guide",
    [Language.KR]: "가이드",
  },
  LOGOUT: {
    [Language.EN]: "Logout",
    [Language.KR]: "로그아웃",
  },
  SUBMIT: {
    [Language.EN]: "Submit",
    [Language.KR]: "보내기",
  },
  CANCEL: {
    [Language.EN]: "Cancel",
    [Language.KR]: "취소",
  },
  SAVE: {
    [Language.EN]: "Save",
    [Language.KR]: "저장",
  },
  ACTIVATE: {
    [Language.EN]: "Activate",
    [Language.KR]: "활성화",
  },
  DEACTIVATE: {
    [Language.EN]: "Deactivate",
    [Language.KR]: "비활성화",
  },
  UNLINK: {
    [Language.EN]: "Disconnect",
    [Language.KR]: "연결 해제",
  },
  BEFORE: {
    [Language.EN]: "<- Before",
    [Language.KR]: "<- 이전",
  },
  LOGIN: {
    [Language.EN]: "Login",
    [Language.KR]: "로그인",
  },
  REGISTER: {
    [Language.EN]: "Register",
    [Language.KR]: "회원가입",
  },
  REQUEST_VENUE_REGISTRATION: {
    [Language.EN]: "Request registration",
    [Language.KR]: "등록 요청",
  },
  CHANGE: {
    [Language.EN]: "Change",
    [Language.KR]: "변경하기",
  },
  IMPORT: {
    [Language.EN]: "Import",
    [Language.KR]: "가져오기",
  },
  APPLY: {
    [Language.EN]: "Apply",
    [Language.KR]: "적용",
  },
  TAG_MATCH_ALL_LABEL: {
    [Language.EN]: "All",
    [Language.KR]: "모두 포함",
  },
  TAG_MATCH_ANY_LABEL: {
    [Language.EN]: "Any",
    [Language.KR]: "일부 포함",
  },
  GENERATE: {
    [Language.EN]: "Generate",
    [Language.KR]: "생성하기",
  },
  SET: {
    [Language.EN]: "Set",
    [Language.KR]: "설정하기",
  },
  FIND: {
    [Language.EN]: "Find",
    [Language.KR]: "찾기",
  },
  REQUEST_ACCESS: {
    [Language.EN]: "Request Access",
    [Language.KR]: "대기 등록",
  },
  MOVE_TO_SUBSCRIBE: {
    [Language.EN]: "Move to Subscribe",
    [Language.KR]: "구독하기",
  },
  MANAGE: {
    [Language.EN]: "Manage",
    [Language.KR]: "구독 관리",
  },
  SUBSCRIBE: {
    [Language.EN]: "Subscribe",
    [Language.KR]: "구독하기",
  },
  INVITE: {
    [Language.EN]: "Invite",
    [Language.KR]: "초대하기",
  },
  INVITING: {
    [Language.EN]: "Inviting…",
    [Language.KR]: "초대 중…",
  },
  ACCEPT: {
    [Language.EN]: "Accept",
    [Language.KR]: "수락",
  },
  REJECT: {
    [Language.EN]: "Reject",
    [Language.KR]: "거절",
  },
  CANCELED: {
    [Language.EN]: "Canceled",
    [Language.KR]: "취소됨",
  },
  RETRY: {
    [Language.EN]: "Retry",
    [Language.KR]: "다시 시도",
  },
  PROJECT_PORTFOLIO: {
    [Language.EN]: "Portfolio",
    [Language.KR]: "포트폴리오",
  },
  PROJECT_FLOOR_PLAN: {
    [Language.EN]: "Floor plan",
    [Language.KR]: "작품 배치도",
  },
};

/**
 * 버튼 이름을 가져오는 함수
 * 언어는 languageStore에서 자동으로 가져옵니다.
 */
export function getBtnName(
  key: BtnNameKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = btnNames[key];

  if (!message) {
    console.warn(`Btn name key "${key}" not found.`);
    return key;
  }

  let translatedMessage = message[language] || message[Language.EN];

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translatedMessage = translatedMessage.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        String(paramValue)
      );
    });
  }

  return translatedMessage;
}
